<script>
export default {
  props: {
    whatDo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      weightOptions: [
        { display: 'Low Priority', value: 1},
        { display: 'Normal', value: 2},
        { display: 'High Priority', value: 3},
      ],
      canvas: null,
      ctx: null,
      rotation: 0,
      isSpinning: false,
      result: null,
      spinVelocity: 0,
      lastTime: 0,
    };
  },
  computed: {
    projects() {
      return this.whatDo.projects || [];
    },
    totalWeight() {
      return this.projects.reduce((sum, p) => sum + (Number(p.weight) || 1), 0);
    }
  },
  mounted() {
    this.canvas = this.$refs.wheelCanvas;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
      this.canvas.width = 340;
      this.canvas.height = 340;   // extra room for top pointer
      this.drawWheel();
    }
  },
  methods: {
    drawWheel(currentRotation = 0) {
      const ctx = this.ctx;
      if (!ctx) return;

      const centerX = this.canvas.width / 2;
      const centerY = this.canvas.height / 2;
      const radius = 155;
      let startAngle = currentRotation;

      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      this.projects.forEach((project, index) => {
        const sliceWeight = Number(project.weight) || 1;
        const sliceAngle = (sliceWeight / this.totalWeight) * Math.PI * 2;
        const endAngle = startAngle + sliceAngle;

        // Slice
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = this.getSliceColor(index);
        ctx.fill();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 6;
        ctx.stroke();

        // Text with wrapping
        this.drawSliceText(project.title, startAngle + sliceAngle / 2, radius, centerX, centerY);

        startAngle = endAngle;
      });

      // Center hub with "SPIN"
      ctx.beginPath();
      ctx.arc(centerX, centerY, 38, 0, Math.PI * 2);
      ctx.fillStyle = '#2c2c2c';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 8;
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('SPIN', centerX, centerY + 6);

      // Pointer at the VERY TOP of the wheel, pointing DOWN
      const pointerY = centerY - radius - 18;
      ctx.beginPath();
      ctx.moveTo(centerX - 22, pointerY + 10);
      ctx.lineTo(centerX + 22, pointerY + 10);
      ctx.lineTo(centerX, pointerY + 38);
      ctx.closePath();
      ctx.fillStyle = '#e74c3c';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      ctx.stroke();
    },

    drawSliceText(title, angle, radius, centerX, centerY) {
      const ctx = this.ctx;
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(angle);

      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px Arial';

      const maxWidth = radius * 0.72;
      const words = title.split(' ');
      let line = '';
      let y = -10;

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && i > 0) {
          ctx.fillText(line.trim(), radius * 0.65, y);
          line = words[i] + ' ';
          y += 22;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), radius * 0.65, y);
      ctx.restore();
    },

    getSliceColor(index) {
      const colors = [
        '#FF6B6B',
        '#4ECDC4',
        '#45B7D1',
        // '#96CEB4',
        '#F39C12',
        // '#FFEEAD',
        '#D4A5A5',
        '#9B59B6',
      ];
      return colors[index % colors.length];
    },

    spinWheel() {
      if (this.isSpinning || this.projects.length === 0) return;
      this.isSpinning = true;
      this.result = null;

      const minSpins = 3;
      // const extraSpin = Math.random() * 2.5 + 1.5; // between 1.5 and 4 extra spins
      const extraSpin = Math.random() * 5.5 + 1.5; // between 1.5 and 7 extra spins
      this.spinVelocity = (minSpins + extraSpin) * Math.PI * 2 * 0.75;

      this.animateSpin();
    },

    animateSpin(timestamp = 0) {
      if (!this.lastTime) this.lastTime = timestamp;
      const delta = Math.min(timestamp - this.lastTime, 32);
      this.lastTime = timestamp;

      this.spinVelocity *= 0.955;   // faster stop
      this.rotation += this.spinVelocity * (delta / 16);

      this.drawWheel(this.rotation);

      if (this.spinVelocity > 0.018) {
        requestAnimationFrame((ts) => this.animateSpin(ts));
      } else {
        this.isSpinning = false;
        this.findWinner();
      }
    },

    findWinner() {
      let normalized = this.rotation % (Math.PI * 2);
      if (normalized < 0) normalized += Math.PI * 2;

      let currentAngle = 0;
      // Adjusted for top-down pointer (0 is at the top)
      const targetAngle = (Math.PI * 2 - normalized) % (Math.PI * 2);

      for (let i = 0; i < this.projects.length; i++) {
        const project = this.projects[i];
        const sliceWeight = Number(project.weight) || 1;
        const sliceAngle = (sliceWeight / this.totalWeight) * Math.PI * 2;

        if (targetAngle >= currentAngle && targetAngle < currentAngle + sliceAngle) {
          this.result = project;
          return;
        }
        currentAngle += sliceAngle;
      }
      this.result = this.projects[0];
    }
  }
}
</script>

<template>
<div class="d-flex flex-column align-center">

  <!-- show result -->
<!--  <div v-if="result" class="my-8 pa-6 bg-green-lighten-4 rounded-lg">-->
<!--    <h3 class="text-h6 mb-2">The wheel has spoken!</h3>-->
<!--    <p class="text-h5 font-weight-bold">{{ result.title }}</p>-->
<!--    <p class="text-body-1">{{ result.tagline }}</p>-->
<!--    <v-btn @click="result = null"-->
<!--           color="success"-->
<!--           class="mt-4"-->
<!--    >Spin Again</v-btn>-->
<!--  </div>-->

  <!-- show the wheel -->
  <canvas @click="spinWheel"
          ref="wheelCanvas"
          class="cursor-pointer"
  ></canvas>

  <!-- spin button -->
  <v-btn @click="spinWheel"
         color="success"
         size="large"
         class="mt-8"
         :disabled="isSpinning"
  >{{ isSpinning ? 'Spinning...' : 'SPIN THE WHEEL' }}</v-btn>

  <!-- list of projects -->
  <div class="mt-16 mb-8 px-4 w-100">
    <h3 class="mb-4 text-center">{{ whatDo.projects.length }} Projects</h3>

    <div v-for="(p, i) in whatDo.projects" :key="i"
         class="mb-8"
    >
      <p class="my-2 text-capitalize">{{ i+1 }}) <b><u>{{ p.title }}</u></b></p>
      <p class="ms-4 my-2">{{ p.tagline }}</p>
<!--      <p class="ms-4 my-2 text-body-2 font-italic">priority: {{ weightOptions[p.weight] }}</p>-->
    </div>
  </div>

</div>
</template>

<style scoped>
canvas {
  border-radius: 50%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  image-rendering: crisp-edges;
  max-width: 350px;
}
</style>
